import { computed, effect, inject } from '@angular/core';
import { takeUntilDestroyed, toObservable } from '@angular/core/rxjs-interop';
import { DOCUMENT } from '@angular/common';
import { patchState, signalStore, withComputed, withMethods, withState, withHooks } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { pipe, tap } from 'rxjs';
import { FuseConfig, FuseConfigService } from '../../../@fuse/services/config';
import { SystemSettingsStore } from './system-settings.store';
import { UserPreferenceStore } from './user-preference/user-preference.store';
import { normalizeScheme } from './theme.model';
import type { Colors, ThemeState } from './theme.model';
import {
  ACCENT_COLOR,
  brandingVars,
  DEFAULT_SECONDARY_COLOR,
  DEFAULT_SUCCESS_COLOR,
  DEFAULT_WARNING_COLOR,
} from './theme-palette.util';

const initialState: ThemeState = {
  layout: 'classy',
  favicon: null,
  light: {
    logo: null,
    colors: null,
  },
  dark: {
    logo: null,
    colors: null,
  },
  loaded: false,
  isBrowserDarkMode: false,
  currentConfig: null,
};

export const ThemeStore = signalStore(
  { providedIn: 'root' },
  withState<ThemeState>(initialState),
  withComputed(store => {
    const systemSettingsStore = inject(SystemSettingsStore);
    const userPreferenceStore = inject(UserPreferenceStore);

    return {
      currentColors: computed(() => {
        const userScheme = normalizeScheme(userPreferenceStore.user()?.branding?.scheme);
        const branding = systemSettingsStore.branding();

        let scheme: 'light' | 'dark';
        if (userScheme === 'light' || userScheme === 'dark') {
          scheme = userScheme;
        } else {
          // userScheme === 'auto': fall back to browser dark-mode detection
          scheme = store.isBrowserDarkMode() ? 'dark' : 'light';
        }

        const colors = branding?.[scheme].colors;
        if (!colors) return colors;

        return {
          ...colors,
          accent: ACCENT_COLOR[scheme],
          secondary: colors.secondary ?? DEFAULT_SECONDARY_COLOR[scheme],
          success: colors.success ?? DEFAULT_SUCCESS_COLOR[scheme],
          warning: colors.warning ?? DEFAULT_WARNING_COLOR[scheme],
        };
      }),
    };
  }),
  withMethods(store => {
    const document = inject(DOCUMENT);
    const fuseConfigService = inject(FuseConfigService);
    const userPreferenceStore = inject(UserPreferenceStore);

    return {
      initDarkModeObserver: () => {
        const observer = new MutationObserver(() => {
          const hasDarkClass = document.body.classList.contains('dark');
          patchState(store, { isBrowserDarkMode: hasDarkClass });
        });

        observer.observe(document.body, {
          attributes: true,
          attributeFilter: ['class'],
        });

        // Initial check
        const hasDarkClass = document.body.classList.contains('dark');
        patchState(store, { isBrowserDarkMode: hasDarkClass });
      },

      updateConfig: rxMethod<void>(
        pipe(
          tap(() => {
            const userSettings = userPreferenceStore.user();
            if (!userSettings) return;

            const currentConfig = store.currentConfig();
            if (!currentConfig) return;

            const newConfig: FuseConfig = {
              ...currentConfig,
              scheme: normalizeScheme(userSettings.branding?.scheme),
              layout: userSettings.branding.menu === 'Boven' ? 'modern' : 'classy',
            };

            if (JSON.stringify(newConfig) !== JSON.stringify(store.currentConfig())) {
              fuseConfigService.config = newConfig;
              patchState(store, { currentConfig: newConfig });
            }
          })
        )
      ),

      setColors(colors: Colors) {
        if (!colors) return;

        Object.keys(colors).forEach(color => {
          // Which variables exist, what they hold and whether they belong on body or :root is
          // decided in theme-palette.util.ts — shared with .storybook/preview.ts so a story can
          // never render against a different set than the app.
          for (const { target, name, value } of brandingVars(color, colors[color])) {
            setCssVariable(target === 'root' ? ':root' : 'body', name, value);
          }
        });
      },
    };
  }),
  withHooks({
    onInit(store, fuseConfigService = inject(FuseConfigService), userPreferenceStore = inject(UserPreferenceStore)) {
      effect(() => {
        store.setColors(store.currentColors());
      });

      // Initialize dark mode observer
      store.initDarkModeObserver();

      // Subscribe to Fuse config changes
      fuseConfigService.config$.pipe(takeUntilDestroyed()).subscribe((config: FuseConfig) => {
        patchState(store, { currentConfig: config });
        store.setColors(store.currentColors());
      });

      // Set up effect for color changes
      toObservable(userPreferenceStore.user)
        .pipe(takeUntilDestroyed())
        .subscribe(() => {
          store.updateConfig();
        });
    },
  })
);

// Color math lives in theme-palette.util.ts (shared with the Storybook preview); only the
// DOM write helper stays here.
function setCssVariable(selector: string, variableName: string, value: string): void {
  const element =
    selector === ':root'
      ? document.documentElement
      : selector === 'body'
        ? document.body
        : document.querySelector(selector);

  if (element instanceof HTMLElement) {
    element.style.setProperty(variableName, value);
  }
}
